import { useState, useEffect, useRef } from 'react';
import { User } from 'lucide-react';
import { ApiService } from '../services/apiService';

interface UserIdentity {
  email: string | null;
  isDevelopmentMode: boolean;
  isAuthenticated: boolean;
}

export const UserProfile = () => {
  const [userIdentity, setUserIdentity] = useState<UserIdentity | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<number>();
  const apiService = ApiService.getInstance();

  useEffect(() => {
    const fetchUserIdentity = async () => {
      try {
        setIsLoading(true);
        const identity = await apiService.getUserIdentity();
        setUserIdentity(identity);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch user identity:', err);
        setError('Failed to load user info');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserIdentity();
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 200);
  };

  const handleLogout = () => {
    // Immediately redirect to logout - don't update state or wait
    // This prevents the component from interfering with the navigation
    apiService.logout();
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div data-component="UserProfile" className="relative">
        <div className="w-9 h-9 rounded-full bg-gray-200 animate-pulse flex items-center justify-center">
          <User className="w-5 h-5 text-gray-400" />
        </div>
      </div>
    );
  }

  if (error || !userIdentity) {
    return null;
  }

  const displayEmail = userIdentity.email || 'Not authenticated';
  const initials = userIdentity.email
    ? userIdentity.email.substring(0, 2).toUpperCase()
    : '?';

  return (
    <div
      data-component="UserProfile"
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* User Avatar */}
      <div
        className={`w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-all ${
          userIdentity.isAuthenticated
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
        }`}
        title={displayEmail}
      >
        {userIdentity.isAuthenticated ? (
          <span className="text-sm font-semibold">{initials}</span>
        ) : (
          <User className="w-5 h-5" />
        )}
      </div>

      {/* Hover Popup */}
      {isHovered && (
        <div
          data-flow="user-info-popup"
          className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex items-start gap-3">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                userIdentity.isAuthenticated
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}
            >
              {userIdentity.isAuthenticated ? (
                <span className="text-lg font-semibold">{initials}</span>
              ) : (
                <User className="w-6 h-6" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {userIdentity.isAuthenticated ? 'Authenticated User' : 'Guest User'}
              </div>
              <div className="text-xs text-gray-600 truncate mt-1" title={displayEmail}>
                {displayEmail}
              </div>
              {userIdentity.isDevelopmentMode && (
                <div className="mt-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded inline-block">
                  Development Mode
                </div>
              )}
            </div>
          </div>

          {/* Logout Button */}
          {userIdentity.isAuthenticated && (
            <button
              onClick={handleLogout}
              className="mt-3 w-full px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
              data-flow="logout-button"
            >
              Logout
            </button>
          )}
        </div>
      )}
    </div>
  );
};
